import type { UnknownAction } from '@reduxjs/toolkit';
import type { InvalidateMessage } from '@skynet/data-ws-core';

/**
 * Minimum interval between identical invalidations (same entity/action/id) so
 * a burst of backend events doesn't trigger a refetch storm. Shared by every
 * realtime WS channel (assistant, auth, smm).
 *
 * A burst is handled on both edges: the first frame invalidates immediately
 * (leading) and, if further identical frames arrive inside the window, one
 * more invalidation runs when the window closes (trailing). Without the
 * trailing edge the last change of a burst can be missed when its refetch
 * landed before the change was committed.
 *
 * The rate cap is unaffected: a sustained stream still produces one
 * invalidation per window, it just lands at the window's end rather than its
 * start (plus the very first leading one).
 */
export const INVALIDATE_DEBOUNCE_MS = 2000;

/** Prune the debounce map once it grows past this many keys. */
const DEBOUNCE_MAP_PRUNE_SIZE = 200;

/**
 * Actions mapped from one `Invalidate` frame, split by whether dispatching
 * them twice for the same frame is safe.
 *
 * The split is what keeps {@link debounceInvalidate}'s trailing edge honest:
 * only `cache` is ever replayed, so a non-idempotent action cannot reach the
 * trailing edge by being appended to a flat list of actions — a mapper has to
 * declare which bucket it belongs to.
 */
export interface MappedInvalidations {
  /**
   * RTK Query tag invalidations. Idempotent — the worst a repeat costs is a
   * refetch — so these run on both edges of a burst.
   */
  cache: UnknownAction[];
  /**
   * Everything else a frame maps to: slice updates such as the exchange sync
   * spinner flags. Dispatched on the leading edge only, because a replay a
   * window later can contradict a newer frame — a repeated `sync-started`
   * would re-raise a spinner the `sync-finished` of a fast sync has already
   * cleared, and a repeated `sync-finished` would clear the spinner of a sync
   * that started inside the same window. Anything that is not safe to run
   * twice belongs here.
   */
  state?: UnknownAction[];
}

interface DebounceEntry {
  /** Server `ts` of the most recently seen frame for this key. */
  lastTs: number;
  /** Client clock when the current window opened. */
  windowStart: number;
  /** Pending trailing timer, armed by a duplicate inside the window. */
  trailingTimer: ReturnType<typeof setTimeout> | null;
  /** Cache invalidations of the latest duplicate, run by that timer. */
  trailing: (() => void) | null;
}

const debounceState = new Map<string, DebounceEntry>();

const clearEntryTimer = (entry: DebounceEntry) => {
  if (entry.trailingTimer !== null) {
    clearTimeout(entry.trailingTimer);
    entry.trailingTimer = null;
  }
};

const fireTrailing = (key: string) => {
  const entry = debounceState.get(key);

  if (entry === undefined) {
    return;
  }

  const trailing = entry.trailing;
  entry.trailingTimer = null;
  entry.trailing = null;
  // Re-open the window from now so a still-continuing burst doesn't arm a
  // trailing for every frame; `lastTs` stays on the latest seen frame.
  entry.windowStart = Date.now();

  trailing?.();
};

/** Test helper: clears the debounce state and pending trailing timers. */
export const resetInvalidateDebounce = () => {
  for (const entry of debounceState.values()) {
    clearEntryTimer(entry);
  }
  debounceState.clear();
};

/**
 * Dispatches what a channel's mapper made of one `Invalidate` frame,
 * debounced per channel/entity/action/id over {@link INVALIDATE_DEBOUNCE_MS}:
 *
 * - the first frame dispatches everything right away (leading edge);
 * - an identical frame inside the window dispatches nothing of its own but
 *   arms a single trailing edge, which replays that frame's `cache` when the
 *   window closes — so the burst's last change isn't lost to a refetch that
 *   landed before it was committed;
 * - an out-of-order frame older than the last one seen is dropped;
 * - a frame that maps to no actions opens no window at all.
 *
 * Window membership is decided on the server-side `ts` (immune to client
 * clock drift), falling back to the client clock for an unparseable one,
 * while the trailing delay necessarily runs on the client clock. Skew between
 * the two only shifts a trailing slightly early or late: the leading edge
 * still fires, and a negative remainder is clamped to an immediate trailing.
 *
 * Note: a numeric `id: 0` and a missing `id` produce different keys on
 * purpose — an event about record 0 is not the same as a collection-wide
 * event.
 *
 * @param channel - Namespaces the debounce key so equally-named entities from
 *   different channels never collide.
 * @param msg - The invalidation frame being handled.
 * @param mapped - The actions the channel's mapper made of that frame.
 * @param dispatch - Store dispatch to run them through.
 */
export const debounceInvalidate = (
  channel: string,
  msg: InvalidateMessage,
  { cache, state = [] }: MappedInvalidations,
  dispatch: (action: UnknownAction) => unknown,
): void => {
  if (cache.length === 0 && state.length === 0) {
    // An unknown entity, or a frame the mapper can't act on: there is nothing
    // to dispatch now or later, so don't open a window (or grow the map).
    return;
  }

  const parsedTs = Date.parse(msg.ts);
  const now = Date.now();
  const eventTs = Number.isNaN(parsedTs) ? now : parsedTs;

  const key = `${channel}:${msg.entity}:${msg.action}:${msg.id ?? ''}`;
  const entry = debounceState.get(key);

  if (entry !== undefined && eventTs < entry.lastTs) {
    // Out-of-order frame older than the last one seen: whatever it changed is
    // already covered, either by that newer frame's invalidation or by the
    // trailing it armed, so this one neither dispatches nor arms anything.
    return;
  }

  if (entry !== undefined && eventTs - entry.lastTs < INVALIDATE_DEBOUNCE_MS) {
    // Duplicate inside the window: hold its cache invalidations for the
    // trailing edge rather than dispatching anything now.
    entry.lastTs = eventTs;
    entry.trailing = () => {
      for (const cacheAction of cache) {
        dispatch(cacheAction);
      }
    };

    if (entry.trailingTimer === null) {
      const remaining = Math.max(0, INVALIDATE_DEBOUNCE_MS - (now - entry.windowStart));
      entry.trailingTimer = setTimeout(() => {
        fireTrailing(key);
      }, remaining);
    }

    return;
  }

  // First frame, or the next one after the window closed: dispatch now.
  if (entry === undefined) {
    debounceState.set(key, {
      lastTs: eventTs,
      windowStart: now,
      trailingTimer: null,
      trailing: null,
    });
  } else {
    clearEntryTimer(entry);
    entry.lastTs = eventTs;
    entry.windowStart = now;
    entry.trailing = null;
  }

  // Keep the map bounded: entity:action:id combinations are finite in
  // practice, but prune expired entries once in a while to be safe
  if (debounceState.size >= DEBOUNCE_MAP_PRUNE_SIZE) {
    for (const [k, e] of debounceState) {
      if (e.trailingTimer === null && eventTs - e.lastTs >= INVALIDATE_DEBOUNCE_MS) {
        debounceState.delete(k);
      }
    }
  }

  // State before cache, so a spinner flag is up before its refetch starts.
  for (const stateAction of state) {
    dispatch(stateAction);
  }

  for (const cacheAction of cache) {
    dispatch(cacheAction);
  }
};
